import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useIsMobile } from '../hooks/useMobile';

const LAYOUT_COOKIE_NAME = "layout:sidebar-state";
const LAYOUT_COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days
const LAYOUT_KEYBOARD_SHORTCUT = "b";

interface LayoutContextType {
  // Sidebar state
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  setSidebarOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;

  // Mobile state
  isMobile: boolean;
  mobileSidebarOpen: boolean;
  setMobileSidebarOpen: (open: boolean) => void;

  // Navbar state (for future expansion)
  navbarHeight: number;
}

const LayoutContext = createContext<LayoutContextType | null>(null);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

interface LayoutProviderProps {
  children: React.ReactNode;
  defaultSidebarOpen?: boolean;
  defaultSidebarCollapsed?: boolean;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({
  children,
  defaultSidebarOpen = true,
  defaultSidebarCollapsed = false,
}) => {
  const { isMobile } = useIsMobile();

  // Sidebar state for desktop
  const [sidebarOpen, setSidebarOpen] = useState(defaultSidebarOpen);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(defaultSidebarCollapsed);

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Navbar configuration
  const navbarHeight = 64; // 16 * 4 = 64px (h-16)

  // Load saved sidebar state from cookie on mount
  useEffect(() => {
    const savedState = document.cookie
      .split('; ')
      .find(row => row.startsWith(LAYOUT_COOKIE_NAME + '='));

    if (savedState) {
      const state = savedState.split('=')[1];
      try {
        const { open, collapsed } = JSON.parse(decodeURIComponent(state));
        setSidebarOpen(open);
        setSidebarCollapsed(collapsed);
      } catch (e) {
        // Ignore invalid cookie data
      }
    }
  }, []);

  // Save sidebar state to cookie whenever it changes
  const saveSidebarState = useCallback((open: boolean, collapsed: boolean) => {
    const state = JSON.stringify({ open, collapsed });
    document.cookie = `${LAYOUT_COOKIE_NAME}=${encodeURIComponent(state)}; path=/; max-age=${LAYOUT_COOKIE_MAX_AGE}`;
  }, []);

  // Enhanced setSidebarOpen that saves state
  const handleSetSidebarOpen = useCallback((open: boolean) => {
    setSidebarOpen(open);
    saveSidebarState(open, sidebarCollapsed);
  }, [sidebarCollapsed, saveSidebarState]);

  // Enhanced setSidebarCollapsed that saves state
  const handleSetSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    saveSidebarState(sidebarOpen, collapsed);
  }, [sidebarOpen, saveSidebarState]);

  // Toggle sidebar based on current state and device
  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileSidebarOpen(prev => !prev);
    } else {
      if (sidebarOpen) {
        // If open, collapse it
        handleSetSidebarCollapsed(!sidebarCollapsed);
      } else {
        // If closed, open it
        handleSetSidebarOpen(true);
      }
    }
  }, [isMobile, sidebarOpen, sidebarCollapsed, handleSetSidebarOpen, handleSetSidebarCollapsed]);

  // Keyboard shortcut for toggling sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === LAYOUT_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const contextValue: LayoutContextType = {
    sidebarOpen,
    sidebarCollapsed,
    setSidebarOpen: handleSetSidebarOpen,
    setSidebarCollapsed: handleSetSidebarCollapsed,
    toggleSidebar,
    isMobile,
    mobileSidebarOpen,
    setMobileSidebarOpen,
    navbarHeight,
  };

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};